import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Radio,
  Chip,
  InputAdornment,
  Tooltip,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PepwiseToast from 'components/Widgets/PepwiseToast';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AdvancedFiltersCard } from 'components/PanelFilters';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import DeleteConfirmationModal from 'components/Modal/DeleteConfirmationModal';
import { snowUrl } from 'service/config';
import { createSop, updateSop, attachAlertsSop, getSopList, deleteSop } from 'service/unifiedAlerts';
import fontSizeResponsive from 'assets/theme/functions/fontSizeResponsive';
import { useTheme } from "@mui/material/styles";
import moment from 'moment';
import { getDecodedToken } from 'service/service';
import AddEditSopModal from './AddEditSopModal';

export interface ManageSopEntityFetchArgs {
  filters: any;
  page: number;
  pageSize: number;
  sortingInfo: any;
  activeTile?: string;
  activeParentTile?: string;
  type: string;
  searchCursor?: Array<string | number> | null;
  direction?: 'next' | 'prev';
}

export interface ManageSopEntityFetchResponse {
  rows: any[];
  total: number;
  pagination?: {
    pageSize?: number;
    total?: number;
    hasNext?: boolean;
    hasPrevious?: boolean;
    firstSortValues?: Array<string | number> | null;
    lastSortValues?: Array<string | number> | null;
    [key: string]: any;
  };
}

type SortValues = Array<string | number>;

interface AlertPageRequest {
  searchCursor: SortValues | null;
  direction: 'next' | 'prev';
}

interface AlertPageCursor {
  signature: string;
  page: number;
  firstSortValues: SortValues | null;
  lastSortValues: SortValues | null;
  request: AlertPageRequest;
}

const FIRST_ALERT_PAGE_REQUEST: AlertPageRequest = { searchCursor: null, direction: 'next' };

const getAlertPageRequest = (
  cursor: AlertPageCursor | null,
  signature: string,
  targetPage: number
): AlertPageRequest | null => {
  if (!cursor || cursor.signature !== signature) {
    return targetPage === 1 ? FIRST_ALERT_PAGE_REQUEST : null;
  }
  if (targetPage === cursor.page + 1 && cursor.lastSortValues) {
    return { searchCursor: cursor.lastSortValues, direction: 'next' };
  }
  if (targetPage === cursor.page - 1 && cursor.firstSortValues) {
    return { searchCursor: cursor.firstSortValues, direction: 'prev' };
  }
  if (targetPage === 1) return FIRST_ALERT_PAGE_REQUEST;
  if (targetPage === cursor.page) return cursor.request;
  return null;
};

export interface ManageSopEntityConfig {
  title: string;
  stepOneLabel: string;
  selectedItemsLabel: string;
  tableKey: string;
  tableType: string;
  defaultColumnNames: string[];
  initialFilters: Record<string, any[]>;
  filterOptions: Record<string, string[]>;
  disabledKeys?: string[];
  defaultSorting: any;
  getRowId: (row: any) => string;
  getRowLabel?: (row: any) => string;
  getColumns: (args: {
    rows: any[];
    selectedIds: string[];
    setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>;
    colors: any;
    autoColumnWidth: (min?: number, flex?: number, max?: number) => string;
    snowUrl: string;
  }) => any[];
  fetchRows: (args: ManageSopEntityFetchArgs) => Promise<ManageSopEntityFetchResponse>;
  normalizeFilters?: (filters: any) => any;
}

interface ManageSopDialogProps {
  open: boolean;
  onClose: () => void;
  type: string;
  activeTile?: string;
  activeParentTile?: string;
  onSopAttached?: () => void;
  entityConfig: ManageSopEntityConfig;
}

interface SopRow {
  id: number | string;
  name: string;
  sopName?: string;
  link?: string;
  owner?: string;
  assignedApprover?: string;
  approvalRequired?: boolean;
  version?: string;
  status?: string;
  createdBy?: string;
  createdByEmail?: string;
  updatedBy?: string;
  updatedByEmail?: string;
  updatedAt?: string;
  lastUpdated?: string;
  isNew?: boolean;
}

const autoColumnWidth = (min = 140, flex = 1, max = min * 1.5) =>
  `minmax(clamp(${min}px, ${min / 10}vw, ${max}px), ${flex}fr)`;

const formatLastUpdated = (date?: string | Date | null) => {
  if (!date) return '-';
  const m = moment(date);
  return m.isValid() ? m.format('MM-DD-YYYY HH:mm') : '-';
};

const ManageSopDialog: React.FC<ManageSopDialogProps> = ({ open, onClose, type, activeTile, activeParentTile, onSopAttached, entityConfig }) => {
  const theme = useTheme();
  const colors = theme.palette;
  const resolvedGetRowId = entityConfig.getRowId;
  const resolvedStepOneLabel = entityConfig.stepOneLabel;
  const resolvedSelectedItemsLabel = entityConfig.selectedItemsLabel;
  const resolvedDefaultSorting = useMemo(
    () => entityConfig.defaultSorting,
    [entityConfig]
  );
  const [activeStep, setActiveStep] = useState(1);
  const [selectedSop, setSelectedSop] = useState<number | string | null>(null);
  const [pageSop, setPageSop] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortingSopInfo, setSortingSopInfo] = useState({ sortBy: '', sortDir: '' });
  const [currSopFilter, setCurrSopFilter] = useState<Record<string, any>>({});
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedAlerts, setSelectedAlerts] = useState<string[]>([]);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [sopName, setSopName] = useState('');
  const [sopLink, setSopLink] = useState('');
  const [sopOwner, setSopOwner] = useState('');
  const [sopApprovalRequired, setSopApprovalRequired] = useState(true);
  const [searchSop, setSearchSop] = useState('');
  const [editingSopId, setEditingSopId] = useState<number | string | null>(null);
  const [sops, setSops] = useState<SopRow[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [toastTitle, setToastTitle] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const triggerToast = (title: string, message: string, severity: 'success' | 'error' = 'success') => {
    setToastTitle(title);
    setSuccessMessage(message);
    setSnackbarSeverity(severity);
    setShowSuccess(true);
  };
  const [sopsLoading, setSopsLoading] = useState(false);
  const [sopActionLoading, setSopActionLoading] = useState(false);
  const [deletingSopId, setDeletingSopId] = useState<number | string | null>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sopToDelete, setSopToDelete] = useState<SopRow | null>(null);
  const [, setSopsFetched] = useState(false);
  const [sopSearchTouched, setSopSearchTouched] = useState(false);
  const [sopAlerts, setSopAlerts] = useState<any[]>([]);
  const [sopTotalAlerts, setSopTotalAlerts] = useState(0);
  const [sopAlertsLoading, setSopAlertsLoading] = useState(false);
  const [sopAlertsPage, setSopAlertsPage] = useState(1);
  const [sopAlertsPageSize, setSopAlertsPageSize] = useState(10);
  const [sopAlertsSortingInfo, setSopAlertsSortingInfo] = useState(resolvedDefaultSorting);
  const [sopAppliedFilters, setSopAppliedFilters] = useState<any>(null);
  const [isAddSopModalOpen, setIsAddSopModalOpen] = useState(false);
  const alertPageCursorRef = useRef<AlertPageCursor | null>(null);
  const sopAlertsRequestIdRef = useRef(0);

  const currentUserEmail = useMemo(() => {
    try {
      const token = getDecodedToken();
      return (token?.email || '').toLowerCase().trim();
    } catch {
      return '';
    }
  }, []);

  const currentUserId = useMemo(() => {
    try {
      const token = getDecodedToken();
      return String(token?.userId || token?._id || token?.id || '').trim();
    } catch {
      return '';
    }
  }, []);

  const isCreatorOfSop = useCallback((row: SopRow) => {
    try {
      const token = getDecodedToken();
      const userEmail = (token?.email || currentUserEmail || '').toLowerCase().trim();
      const userId = String(token?.userId || token?._id || token?.id || currentUserId || '').trim();

      const creatorEmail = (row.createdByEmail || '').toLowerCase().trim();
      const creatorId = String(row.createdBy || '').trim();

      return Boolean(
        (userEmail && creatorEmail && creatorEmail === userEmail) ||
        (userEmail && creatorId && creatorId.toLowerCase() === userEmail) ||
        (userId && creatorId && creatorId === userId)
      );
    } catch {
      return false;
    }
  }, [currentUserEmail, currentUserId]);

  const isApproverOfSop = useCallback((row: SopRow) => {
    try {
      const token = getDecodedToken();
      const userEmail = (token?.email || currentUserEmail || '').toLowerCase().trim();
      const userId = String(token?.userId || token?._id || token?.id || currentUserId || '').trim();

      const approver = (row.assignedApprover || row.owner || '').toLowerCase().trim();
      if (!approver) return false;

      return Boolean(
        (userEmail && approver === userEmail) ||
        (userId && approver === userId)
      );
    } catch {
      return false;
    }
  }, [currentUserEmail, currentUserId]);

  const canDeleteSop = useCallback((row: SopRow) => {
    const isApproved = !row.status || /approved/i.test(row.status);
    const isCreator = isCreatorOfSop(row);
    const approver = (row.assignedApprover || row.owner || '').trim();

    return isApproved ? (approver ? isApproverOfSop(row) : isCreator) : isCreator;
  }, [isCreatorOfSop, isApproverOfSop]);

  useEffect(() => {
    if (open) {
      setActiveStep(1);
      setSelectedAlerts([]);
      setFiltersApplied(false);
      setSopName('');
      setSopLink('');
      setSearchSop('');
      setEditingSopId(null);
      setSops([]);
      setSelectedSop(null);
      setSopsLoading(false);
      setSopActionLoading(false);
      setDeletingSopId(null);
      setSopSearchTouched(false);
      setSopAlerts([]);
      setSopTotalAlerts(0);
      setSopAlertsLoading(false);
      setSopAlertsPage(1);
      setSopAlertsPageSize(10);
      setSopAlertsSortingInfo(resolvedDefaultSorting);
      setSopAppliedFilters(null);
      alertPageCursorRef.current = null;
      sopAlertsRequestIdRef.current = 0;
      setIsAddSopModalOpen(false);
    }
  }, [open, resolvedDefaultSorting]);

  const loadSops = async (
    searchValue = searchSop.trim(),
    currentPage = pageSop,
    currentRowsPerPage = rowsPerPage,
    currentSortingInfo = sortingSopInfo
  ) => {
    setSopsLoading(true);

    try {
      const response = await getSopList(
        type,
        currentPage,
        currentRowsPerPage,
        currentSortingInfo,
        searchValue,
        'Approved'
      );

      const responseData = response.data.docs;

      setSops(responseData.map((sop: any) => ({
        id: sop._id,
        name: sop.sopName,
        sopName: sop.sopName,
        link: sop.sopLink,
        owner: sop.assignedApprover,
        assignedApprover: sop.assignedApprover,
        approvalRequired: sop.approvalRequired,
        version: sop.version,
        status: sop.status,
        createdBy: sop.createdBy,
        createdByEmail: sop.createdByEmail,
        updatedBy: sop.updatedByEmail,
        updatedByEmail: sop.updatedByEmail,
        updatedAt: sop.updatedAt,
        lastUpdated: formatLastUpdated(sop.updatedAt),
      })));

      setSelectedSop(null);
      setSortingSopInfo(currentSortingInfo);
      setPageSop(response.data.page);
      setRowsPerPage(response.data.limit);
      setTotalElements(response.data.totalDocs);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      setSops([]);
      setSelectedSop(null);
      setTotalElements(0);
      setTotalPages(1);
    } finally {
      setSopsLoading(false);
    }
  };

  useEffect(() => {
    if (!open || activeStep !== 2) return;

    const searchValue = searchSop.trim();

    if (searchValue.length > 0 && searchValue.length < 3) return;

    loadSops(searchValue);
  }, [open, activeStep, rowsPerPage, sortingSopInfo, pageSop, type]);

  useEffect(() => {
    if (!open || activeStep !== 2 || !sopSearchTouched) return;

    const searchValue = searchSop.trim();

    if (searchValue.length > 0 && searchValue.length < 3) return;

    const timer = setTimeout(() => {
      if (pageSop !== 1) {
        setPageSop(1);
        return;
      }

      loadSops(searchValue, 1, rowsPerPage, sortingSopInfo);
    }, 500);

    return () => clearTimeout(timer);
  }, [open, activeStep, searchSop, sopSearchTouched, rowsPerPage, sortingSopInfo, type]);

  const initialManageFilters = useMemo(() => entityConfig.initialFilters, [entityConfig]);

  const manageFilterOptions = useMemo(() => entityConfig.filterOptions, [entityConfig]);

  const handleNext = () => setActiveStep(2);
  const handleBack = () => setActiveStep(1);

  const handleOpenAddSopModal = () => {
    setSopName('');
    setSopLink('');
    setSopOwner('');
    setSopApprovalRequired(true);
    setEditingSopId(null);
    setIsAddSopModalOpen(true);
  };

  const handleCloseAddSopModal = () => {
    if (sopActionLoading) return;
    setIsAddSopModalOpen(false);
    setSopName('');
    setSopLink('');
    setSopOwner('');
    setSopApprovalRequired(true);
    setEditingSopId(null);
  };

  const getAlertPageSignature = useCallback(
    (filters: any, pageSize: number, sortingInfo: any) => {
      return JSON.stringify({
        filters,
        pageSize,
        sortingInfo,
        activeTile,
        activeParentTile,
        type,
      });
    },
    [activeTile, activeParentTile, type]
  );

  const loadSopAlerts = async (
    filters: any,
    targetPage = sopAlertsPage,
    currentPageSize = sopAlertsPageSize,
    currentSortingInfo = sopAlertsSortingInfo
  ) => {
    const signature = getAlertPageSignature(filters, currentPageSize, currentSortingInfo);
    const alertPageRequest = getAlertPageRequest(alertPageCursorRef.current, signature, targetPage);

    if (!alertPageRequest) {
      alertPageCursorRef.current = null;
      setSopAlertsPage(1);
      if (targetPage !== 1) {
        loadSopAlerts(filters, 1, currentPageSize, currentSortingInfo);
      }
      return;
    }

    const requestId = ++sopAlertsRequestIdRef.current;
    setSopAlertsLoading(true);

    try {
      const response = await entityConfig.fetchRows({
        filters,
        page: targetPage,
        pageSize: currentPageSize,
        sortingInfo: currentSortingInfo,
        activeTile,
        activeParentTile,
        type,
        searchCursor: alertPageRequest.searchCursor,
        direction: alertPageRequest.direction,
      });

      if (requestId !== sopAlertsRequestIdRef.current) return;

      const rows = response?.rows || [];
      const total = response?.total || 0;
      setSopAlerts(rows);
      setSopTotalAlerts(total);
      setSopAlertsPage(targetPage);

      const pagination = response?.pagination;
      alertPageCursorRef.current = {
        signature,
        page: targetPage,
        firstSortValues: pagination?.firstSortValues ?? null,
        lastSortValues: pagination?.lastSortValues ?? null,
        request: alertPageRequest,
      };
    } catch (error) {
      if (requestId !== sopAlertsRequestIdRef.current) return;
      console.error('Error loading SOP first-step rows:', error);
      setSopAlerts([]);
      setSopTotalAlerts(0);
      alertPageCursorRef.current = null;
    } finally {
      if (requestId === sopAlertsRequestIdRef.current) {
        setSopAlertsLoading(false);
      }
    }
  };

  const handleApply = (filters: any) => {
    const newFilters: any = entityConfig.normalizeFilters
      ? entityConfig.normalizeFilters(filters)
      : filters;

    setSelectedAlerts([]);
    setFiltersApplied(true);
    setSopAlertsPage(1);
    setSopAppliedFilters(newFilters);
    alertPageCursorRef.current = null;
    loadSopAlerts(newFilters, 1, sopAlertsPageSize, sopAlertsSortingInfo);
  };

  const selectedAlertLabels = useMemo(() => {
    const labelMap = new Map<string, string>();

    sopAlerts.forEach((row: any) => {
      const id = resolvedGetRowId(row);
      if (id) {
        labelMap.set(id, entityConfig.getRowLabel?.(row) || id);
      }
    });

    return selectedAlerts.map((id) => ({
      id,
      label: labelMap.get(id) || id
    }));
  }, [sopAlerts, selectedAlerts, entityConfig, resolvedGetRowId]);

  const alertColumns = useMemo(() => {
    return entityConfig.getColumns({
      rows: sopAlerts,
      selectedIds: selectedAlerts,
      setSelectedIds: setSelectedAlerts,
      colors,
      autoColumnWidth,
      snowUrl,
    });
  }, [sopAlerts, selectedAlerts, entityConfig, colors]);

  const defaultColumns = entityConfig.defaultColumnNames;

  const isAllAlertsSelected = sopAlerts.length > 0 && selectedAlerts.length === sopAlerts.map((row: any) => resolvedGetRowId(row)).filter(Boolean).length;

  const getSnackbarMessage = (response: any, fallbackMessage?: string) => {
    const responseData = response?.data;

    if (typeof responseData === 'string') return responseData;

    if (responseData?.data?.message) return responseData.data.message;
    if (typeof responseData?.data === 'string') return responseData.data;

    return responseData?.message || fallbackMessage;
  };

  const getSnackbarSeverity = (response: any): 'success' | 'error' => {
    return response?.success === false ? 'error' : 'success';
  };


  const handleAddOrUpdateSop = async (
    nameParam?: string,
    linkParam?: string,
    approvalRequiredParam?: boolean,
    ownerParam?: string
  ) => {
    const currentName = (nameParam ?? sopName).trim();
    const currentLink = (linkParam ?? sopLink).trim();
    const currentApprovalRequired = approvalRequiredParam !== undefined ? approvalRequiredParam : sopApprovalRequired;
    const currentOwner = (ownerParam !== undefined ? ownerParam : sopOwner).trim();
    const approverVal = currentApprovalRequired ? currentOwner : null;

    if (!currentName || !currentLink || (currentApprovalRequired && !currentOwner) || sopActionLoading) return;

    setSopActionLoading(true);

    try {
      let selectedSopId = editingSopId;

      if (editingSopId) {
        const response = await updateSop(
          String(editingSopId),
          currentName,
          currentLink,
          type,
          approverVal,
          currentApprovalRequired
        );
        if (response?.success !== true) {
          const errData = response?.response?.data || response;
          triggerToast('Failed to Update SOP.', getSnackbarMessage(errData) || 'Failed to update SOP', 'error');
          setSopActionLoading(false);
          return;
        }
        triggerToast('SOP Updated Successfully.', getSnackbarMessage(response) || 'SOP has been updated successfully.');
        setEditingSopId(null);
      } else {
        const response = await createSop(
          currentName,
          currentLink,
          type,
          approverVal,
          currentApprovalRequired
        );
        if (response?.success !== true) {
          const errData = response?.response?.data || response;
          triggerToast('Failed to Create SOP.', getSnackbarMessage(errData) || 'Failed to create SOP', 'error');
          setSopActionLoading(false);
          return;
        }
        if (currentApprovalRequired) {
          triggerToast(
            'SOP Submitted for Approval.',
            'SOP has been submitted for approval. It will be available for assignment once approved.'
          );
        } else {
          triggerToast('SOP Created Successfully.', getSnackbarMessage(response) || 'SOP has been created successfully.');
        }
      }

      await loadSops();

      setSelectedSop(selectedSopId);
      setSopName('');
      setSopLink('');
      setSopOwner('');
      setSopApprovalRequired(true);
      setIsAddSopModalOpen(false);
    } catch (error: any) {
      triggerToast('Error', getSnackbarMessage(error?.response) || 'Something went wrong', 'error');
      console.error('SOP save failed', error);
    } finally {
      setSopActionLoading(false);
    }
  };

  const handleEditSop = (sop: SopRow) => {
    setSopName(sop.name || '');
    setSopLink(sop.link || '');
    setSopOwner(sop.assignedApprover || sop.owner || '');
    setSopApprovalRequired(sop.approvalRequired !== undefined ? sop.approvalRequired : true);
    setEditingSopId(sop.id);
    setIsAddSopModalOpen(true);
  };

  const handleDeleteClick = (row: SopRow) => {
    if (!canDeleteSop(row)) return;
    setSopToDelete(row);
    setDeleteModalOpen(true);
  };

  const confirmDeleteSop = async () => {
    if (!sopToDelete || deletingSopId || sopActionLoading) return;

    const id = sopToDelete.id;
    setDeleteModalOpen(false);
    setDeletingSopId(id);

    try {
      const response = await deleteSop(String(id));
      triggerToast('SOP Deleted Successfully.', getSnackbarMessage(response, 'SOP has been deleted successfully.'));

      await loadSops();

      if (selectedSop === id) {
        setSelectedSop(null);
      }

      if (editingSopId === id) {
        setSopName('');
        setSopLink('');
        setEditingSopId(null);
      }
    } catch (error: any) {
      triggerToast('Failed to Delete SOP.', getSnackbarMessage(error?.response) || 'Failed to delete SOP', 'error');
      console.error('SOP delete failed', error);
    } finally {
      setDeletingSopId(null);
      setSopToDelete(null);
    }
  };

  const sopColumns = useMemo(() => [
    {
      key: 'select',
      name: 'SELECT',
      width: '60px',
      formatter: ({ row }: { row: SopRow }) => (
        <Radio
          checked={selectedSop === row.id}
          onChange={() => setSelectedSop(row.id)}
          size="small"
          sx={{ color: colors.buttons.primary.disabledBg, '&.Mui-checked': { color: colors.buttons.primary.color } }}
        />
      )
    },
    {
      key: 'sopName',
      name: 'SOP NAME',
      width: autoColumnWidth(260, 2),
      formatter: ({ row }: { row: SopRow }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            sx={{
              color: colors.buttons.primary.color,
              fontWeight: 700,
              textDecoration: row.link ? 'underline' : 'none',
              cursor: row.link ? 'pointer' : 'default',
              fontSize: '0.85rem'
            }}
            onClick={row.link ? () => window.open(row.link, '_blank') : undefined}
          >
            {row.sopName || row.name}
          </Typography>
          {row.isNew && (
            <Chip
                label="New"
                size="small"
                sx={{
                    height: 24,
                    bgcolor: '#ade9c1 !important',
                    color: '#1B7F3A !important',
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    borderRadius: '999px',
                    px: 0,
                    py: 0,
                    '& .MuiChip-label': {
                    px: 0.75,
                    py: 0,
                    lineHeight: '24px',
                    }
                }}
            />
          )}
        </Box>
      )
    },
    { key: 'assignedApprover', name: 'OWNER', width: autoColumnWidth(140), formatter: ({ row }: { row: SopRow }) => row.assignedApprover || row.owner || '-' },
    { key: 'version', name: 'VERSION', width: autoColumnWidth(110), formatter: ({ row }: { row: SopRow }) => row.version || '-' },
    {
      key: 'createdByEmail',
      name: 'CREATED BY',
      width: autoColumnWidth(200, 1.2),
      formatter: ({ row }: { row: SopRow }) => (
        row.createdByEmail || row.createdBy || '-'),
    },
    { key: 'updatedByEmail', name: 'UPDATED BY', width: autoColumnWidth(130), formatter: ({ row }: { row: SopRow }) => row.updatedByEmail || row.updatedBy || '-' },
    { key: 'updatedAt', name: 'LAST UPDATED', width: autoColumnWidth(170), formatter: ({ row }: { row: SopRow }) => row.lastUpdated || formatLastUpdated(row.updatedAt) || '-' },
    {
      key: 'actions',
      name: 'ACTIONS',
      width: autoColumnWidth(120),
      formatter: ({ row }: { row: SopRow }) => {
        const canDelete = canDeleteSop(row);
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {canDelete ? (
              <Tooltip title="Delete SOP" arrow>
                <span>
                  <IconButton size="small" disabled={sopsLoading || sopActionLoading || deletingSopId === row.id} sx={{ color: colors.buttons.primary.deleteButtonBg }} onClick={() => handleDeleteClick(row)}>
                    {deletingSopId === row.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </Box>
        );
      }
    }
  ], [selectedSop, sopsLoading, sopActionLoading, deletingSopId, canDeleteSop]);

  const labelStyle = {
    '& .MuiInputLabel-root': {
      color: `${colors.inputNew.inputPlaceholder} !important`,
      fontWeight: 600,
      fontSize: '0.85rem',
      backgroundColor: colors.inputNew.inputBackground,
      px: 0.5
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: `${colors.inputNew.inputBorderFocus} !important`
    },
    '& .MuiOutlinedInput-root': {
      bgcolor: `${colors.inputNew.inputBackground} !important`,
      height: '40px',
      fontSize: '0.85rem',
      color: `${colors.inputNew.inputText} !important`,
      borderRadius: '4px',
      '& fieldset': {
        borderColor: `${colors.inputNew.inputBorder} !important`
      },
      '&:hover fieldset': {
        borderColor: `${colors.inputNew.inputBorderHover} !important`
      },
      '&.Mui-focused fieldset': {
        borderColor: `${colors.inputNew.inputBorderFocus} !important`,
        borderWidth: '1px !important'
      }
    },
    '& .MuiOutlinedInput-input': {
      color: `${colors.inputNew.inputText} !important`,
      WebkitTextFillColor: `${colors.inputNew.inputText} !important`,
      caretColor: `${colors.inputNew.inputText} !important`,
      backgroundColor: `${colors.inputNew.inputBackground} !important`,
      opacity: '1 !important'
    },
    '& .MuiInputBase-input::placeholder': {
      color: `${colors.inputNew.inputPlaceholder} !important`,
      opacity: '1 !important'
    }
  };

  const handleConfirm = async () => {
    if (!selectedSop || selectedAlerts.length === 0) return;
    try {
        const response = await attachAlertsSop(String(selectedSop), selectedAlerts, type);
        if (response?.success === true) {
          triggerToast('SOP Successfully Mapped.', `${selectedAlerts.length} ${selectedAlerts.length === 1 ? 'alert has' : 'alerts has'} been mapped with SOP.`);
          onSopAttached?.();
        } else {
          triggerToast('Failed to Map SOP.', getSnackbarMessage(response) || 'Failed to map SOP', 'error');
        }
        onClose();
    } catch (error: any) {
        triggerToast('Failed to Map SOP.', getSnackbarMessage(error?.response) || 'Failed to map SOP', 'error');
        console.error('Failed to map SOP', error);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth={false}
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: '4px',
            width: { xs: '80%', sm: '80%', md: '75%' },
            minWidth: '70vw',
            maxWidth: '75vw',
            minHeight: '85vh !important',
            '& .MuiDialogContent-root': {
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        {/* Header */}
        <DialogTitle sx={{
          bgcolor: colors.modal.titleBg,
          color: 'white',
          p: 2,
          px: 4,
          display: 'flex',
          alignItems: 'center',
        }}>
          <Typography sx={{ fontWeight: 600, fontSize: '1.25rem', letterSpacing: '0.5px' }}>
            {`Manage SOP for ${activeTile} ${(type === 'Incidents' || type === 'EWS') && activeTile !== 'Closed' ? '- Open/Reopen' : ''}`}
          </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {/* Stepper moved to the right */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mr: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={handleBack}>
            {activeStep > 1 ? (
              <>
                <ArrowBackIcon sx={{ color: 'white', fontSize: '1.75rem', flexShrink: 0 }}/>
                <CheckCircleIcon sx={{ color: colors.modal.sectionTitle, fontSize: '1.75rem', flexShrink: 0 }} />
              </>
            ) : (
              <Box sx={{
                width: '32px !important',
                height: '32px !important',
                borderRadius: '50% !important',
                backgroundColor: `${colors.modal.background} !important`,
                color: `${colors.modal.subTitle} !important`,
                display: 'flex !important',
                alignItems: 'center !important',
                justifyContent: 'center !important',
                fontSize: '1rem !important',
                fontWeight: '800 !important',
                flexShrink: 0,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>1</Box>
            )}
            <Typography variant="body2" sx={{ fontWeight: activeStep === 1 ? 700 : 400, color: colors.modal.title, fontSize: '0.95rem' }}>
              {resolvedStepOneLabel}
            </Typography>
          </Box>
          <Box sx={{ width: 50, height: 1.5, bgcolor: 'rgba(255,255,255,0.3)' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: '32px !important',
              height: '32px !important',
              borderRadius: '50% !important',
              backgroundColor: activeStep === 2 ? `${colors.modal.background} !important` : 'rgba(255,255,255,0.2) !important',
              color: activeStep === 2 ? `${colors.modal.subTitle} !important` : 'rgba(255,255,255,0.5) !important',
              display: 'flex !important',
              alignItems: 'center !important',
              justifyContent: 'center !important',
              fontSize: '1rem !important',
              fontWeight: '800 !important',
              flexShrink: 0
            }}>{activeStep === 2 ? '2' : '2'}</Box>
            <Typography variant="body2" sx={{
              fontWeight: activeStep === 2 ? 700 : 400,
              color: activeStep === 2 ? colors.modal.title : 'rgba(255,255,255,0.4)',
              fontSize: '0.95rem'
            }}>
              Update SOP
            </Typography>
          </Box>
        </Box>

        <IconButton
            onClick={onClose}
            sx={{
                color: '#ffffff !important',
                p: 0.5,
                '& svg': {
                color: '#ffffff !important',
                },
            }}
        >
            <CloseIcon fontSize="large" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, bgcolor: colors.modal.bg }}>
        {activeStep === 1 ? (
          /* STEP 1: SELECT ALERTS */
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', mt: 2, height: '100%' }}>

            <AdvancedFiltersCard
              onApply={handleApply}
              title="Advance Filters"
              filterOptions={manageFilterOptions}
              initialFilters={initialManageFilters}
              disabledKeys={entityConfig.disabledKeys || []}
              sx={{ mb: 3, border: '1px solid #eef2f6', boxShadow: '0 2px 4px rgba(0,0,0,0.03)', borderRadius: '6px' }}
            />

            {!filtersApplied ? (
              <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'white', border: '1px dashed #ccc', borderRadius: '6px' }}>
                <Typography color="text.secondary" sx={{ fontWeight: 500 }}>No data is available , please apply filters to show data</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: 'white',
                  borderRadius: '4px',
                }}
              >
                <CustomizedDataGrid
                  key={entityConfig.tableKey}
                  tableColumns={alertColumns}
                  tableData={sopAlerts}
                  tableType={entityConfig.tableType}
                  defaultColumnNames={defaultColumns}
                  listLength={sopTotalAlerts}
                  page={sopAlertsPage}
                  pageSize={sopAlertsPageSize}
                  paginationData={{ totalElements: sopTotalAlerts, totalPages: Math.ceil(sopTotalAlerts / sopAlertsPageSize) }}
                  onSorting={(sortInfo: any) => {
                    setSopAlertsSortingInfo(sortInfo);
                    alertPageCursorRef.current = null;

                    if (sopAppliedFilters) {
                      loadSopAlerts(sopAppliedFilters, 1, sopAlertsPageSize, sortInfo);
                    }
                  }}
                  currSorting={sopAlertsSortingInfo}
                  handlePageChange={(_: any, p: number) => {
                    if (sopAppliedFilters) {
                      loadSopAlerts(sopAppliedFilters, p, sopAlertsPageSize, sopAlertsSortingInfo);
                    }
                  }}
                  handlepageSizeChange={(e: any) => {
                    const newPageSize = Number.parseInt(e.target.value);

                    setSopAlertsPageSize(newPageSize);
                    alertPageCursorRef.current = null;

                    if (sopAppliedFilters) {
                      loadSopAlerts(sopAppliedFilters, 1, newPageSize, sopAlertsSortingInfo);
                    }
                  }}
                  isFetching={sopAlertsLoading}
                  isDateRangeFilter={false}
                  isDisabled={false}
                  isShowMoreButton={true}
                  pageSizeOptions={[10, 50, 100, 200, 500, 1000]}
                  isPrevNextPagination
                  isRenderPagination={sopTotalAlerts > 0}
                  isGroupBy={false}
                  isSortIcon={true}
                  headerCheckbox={isAllAlertsSelected}
                  onHeaderSelectAll={(e: any) => {
                    if (e.target.checked) {
                      setSelectedAlerts(sopAlerts.map((row: any) => resolvedGetRowId(row)).filter(Boolean));
                    } else {
                      setSelectedAlerts([]);
                    }
                  }}
                />
                {sopTotalAlerts === 0 && (
                  <Box sx={{ mt: 1, px: 1, py: 1, borderRadius: '4px', backgroundColor: `${theme.palette.table.pagination.BgColor} !important` }}>
                    <Typography variant="bodyText" color={`${theme.palette.table.pagination.text} !important`}>
                      Showing 0 - 0 of 0 results
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        ) : (
          /* STEP 2: UPDATE SOP */
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <TextField
              placeholder="Search SOP"
              fullWidth
              size="small"
              value={searchSop}
              onChange={(e) => {
                setSearchSop(e.target.value);
                setSopSearchTouched(true);
              }}
              inputProps={{ style: { color: colors.inputNew.inputText, caretColor: colors.inputNew.inputText } }}
              sx={{
                mb: 2,
                bgcolor: colors.inputNew.inputBackground,
                '& .MuiOutlinedInput-root': {
                  bgcolor: colors.inputNew.inputBackground,
                  height: '40px',
                  fontSize: '0.85rem',
                  color: `${colors.inputNew.inputText} !important`,
                  '& fieldset': {
                    borderColor: `${colors.inputNew.inputBorder} !important`
                  },
                  '&:hover fieldset': {
                    borderColor: `${colors.inputNew.inputBorderHover} !important`
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: `${colors.inputNew.inputBorderFocus} !important`
                  }
                },
                '& .MuiOutlinedInput-input': {
                  color: colors.inputNew.inputText,
                  WebkitTextFillColor: colors.inputNew.inputText,
                  caretColor: colors.inputNew.inputText
                },
                '& .MuiInputBase-input::placeholder': {
                  color: `${colors.inputNew.inputPlaceholder} !important`,
                  opacity: '1 !important'
                }
              }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: `${colors.inputNew.inputPlaceholder} !important` }} /></InputAdornment> }}
            />

            {selectedAlertLabels.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: '1rem', color: `${colors.inputNew.inputText} !important`, fontWeight: 500, mb: 1 }}>
                  {resolvedSelectedItemsLabel}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    maxHeight: 70,
                    overflowY: 'auto',
                  }}
                >
                  {selectedAlertLabels.map((alert) => (
                    <Chip
                      key={alert.id}
                      title={alert.label}
                      label={alert.label}
                      size='medium'
                      onDelete={() =>
                        setSelectedAlerts((prev) => prev.filter((id) => id !== alert.id))
                      }
                      sx={{
                        height: 34,
                        margin: 0,
                        width: 'auto !important',        // force width to grow
                        minWidth: '50px !important',
                        maxWidth: '100% !important',
                        whiteSpace: 'nowrap !important',
                        overflow: 'visible !important',
                        textOverflow: 'clip !important',
                        backgroundColor: `${colors.filterDropdown.chipBackground} !important`,
                        color: `${colors.filterDropdown.chipText} !important`,
                        '& .MuiChip-label': {
                          paddingRight: '6px !important',
                          whiteSpace: 'nowrap !important',
                          overflow: 'visible !important',
                          textOverflow: 'clip !important',
                        },
                        '& .MuiChip-deleteIcon': {
                          fontSize: '14px !important',
                          color: `${colors.filterDropdown.chipText} !important`,
                          marginLeft: '4px !important',
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

             <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
              {!sopsLoading && sops.length === 0 && searchSop.trim() ? (
                <Box
                sx={{
                  height: "100%",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'white',
                  borderRadius: '4px',
                  border: '1px solid #d8e1ef',
                }}
                >
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: colors.inputNew.inputText }}>
                    No Matching SOPs Found
                    </Typography>
                    </Box>
                    ) : (
              <CustomizedDataGrid
                key="manage-sop-list-grid"
                tableColumns={sopColumns}
                tableData={sops}
                tableType="ManageSopListV2"
                defaultColumnNames={['SELECT', 'SOP NAME', 'VERSION', 'CREATED BY', 'UPDATED BY', 'LAST UPDATED', 'ACTIONS']}
                listLength={totalElements}
                page={pageSop}
                pageSize={rowsPerPage}
                paginationData={{ totalElements: totalElements, totalPages: totalPages }}
                onSorting={setSortingSopInfo}
                currSorting={sortingSopInfo}
                currFilter={currSopFilter}
                onFilterCheck={(f: any) => setCurrSopFilter(f || {})}
                handlePageChange={(_: any, p: number) => setPageSop(p)}
                handlepageSizeChange={(e: any) => {
                  setRowsPerPage(Number.parseInt(e.target.value));
                  setPageSop(1);
                }}
                isFetching={sopsLoading}
                isDateRangeFilter={false}
                isDisabled={false}
                isShowMoreButton={true}
                pageSizeOptions={[10, 50, 100, 200, 500, 1000]}
                isGroupBy={false}
                isSortIcon={true}
                rowHeight={40}
              />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: activeStep === 2 ? 2.5 : 3, px: 4, bgcolor: colors.modal.bg, gap: 3 }}>
        <Button variant="pepwiseButtonText" onClick={onClose} sx={{ color: colors.buttons.primary.color }}>Cancel</Button>
        {activeStep === 2 && (
          <Button
            variant="outlined"
            onClick={handleOpenAddSopModal}
            sx={{
              ...fontSizeResponsive(14),
              whiteSpace: 'nowrap',
              borderColor: `${colors.buttons.primary.borderedBorder} !important`,
              color: `${colors.buttons.primary.borderedText} !important`,
              textTransform: 'uppercase',
              px: 3,
              height: 40,
              fontWeight: 500,
              borderRadius: '4px',
              '&:hover': {
                borderColor: `${colors.buttons.primary.borderedBorder} !important`,
              },
            }}
          >
            ADD NEW SOP
          </Button>
        )}
        <Button
          variant="pepwiseSecondary"
          disabled={activeStep === 1 ? selectedAlerts.length === 0 : !selectedSop}
          onClick={activeStep === 1 ? handleNext : handleConfirm}
          sx={{
            '&:hover': { bgcolor: colors.buttons.primary.bgColor },
            '&.Mui-disabled': { bgcolor: colors.buttons.primary.disabledBg, color: colors.buttons.primary.disabledSolidText }
          }}
        >
          {activeStep === 1 ? 'Update SOP' : 'Confirm'}
        </Button>
      </DialogActions>

    </Dialog>

    {/* ADD / EDIT SOP MODAL */}
    <AddEditSopModal
      open={isAddSopModalOpen}
      onClose={handleCloseAddSopModal}
      editingId={editingSopId}
      initialName={sopName}
      initialLink={sopLink}
      initialOwner={sopOwner}
      initialApprovalRequired={sopApprovalRequired}
      loading={sopActionLoading}
      onSave={async ({ sopName: name, sopLink: link, approvalRequired, owner }) => {
        await handleAddOrUpdateSop(name, link, approvalRequired, owner);
      }}
    />
    
    <DeleteConfirmationModal
      open={deleteModalOpen}
      onClose={() => setDeleteModalOpen(false)}
      onConfirm={confirmDeleteSop}
      title={`Deleting SOP: ${sopToDelete?.name || ''}`}
      type="SOP"
    />

    <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
            top: '72px !important',
            right: '32px !important',
            zIndex: 99999,
        }}
    >
        <Box sx={{ width: '100%', maxWidth: '480px' }}>
            <PepwiseToast
                type={snackbarSeverity}
                title={toastTitle || (snackbarSeverity === 'success' ? 'Success' : 'Error')}
                message={successMessage}
                onClose={() => setShowSuccess(false)}
            />
        </Box>
    </Snackbar>
    </>
  );
};

export default ManageSopDialog;
